/**
 * useVideoPlayer Hook
 *
 * 統一定義影片播放相關狀態，包含播放進度、音量、靜音、倍速等設定。
 * 提供一致的狀態介面，讓 MainPage 可以專注於業務邏輯，
 * 未來也更容易在其他頁面重用或撰寫測試。
 */

import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

export interface UseVideoPlayerConfig {
  /** 初始音量，預設 0.7 */
  defaultVolume?: number;
  /** 初始播放速度，預設 1 */
  defaultPlaybackRate?: number;
}

export interface UseVideoPlayerReturn {
  // ========== 播放狀態 ==========
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  isPlayingRef: MutableRefObject<boolean>;

  // ========== 進度與時間 ==========
  progress: number;
  setProgress: Dispatch<SetStateAction<number>>;
  duration: number;
  setDuration: Dispatch<SetStateAction<number>>;
  playedSeconds: number;
  setPlayedSeconds: Dispatch<SetStateAction<number>>;
  currentFrame: number;
  setCurrentFrame: Dispatch<SetStateAction<number>>;

  // ========== 音量與速度 ==========
  volume: number;
  setVolume: Dispatch<SetStateAction<number>>;
  isMuted: boolean;
  setIsMuted: Dispatch<SetStateAction<boolean>>;
  playbackRate: number;
  setPlaybackRate: Dispatch<SetStateAction<number>>;

  // ========== 其他狀態 ==========
  savedTimecodes: Record<number, string>;
  setSavedTimecodes: Dispatch<SetStateAction<Record<number, string>>>;
  bookmarkEnabled: boolean;
  setBookmarkEnabled: Dispatch<SetStateAction<boolean>>;
  embedUrl: string;
  setEmbedUrl: Dispatch<SetStateAction<string>>;
  settingsOpen: boolean;
  setSettingsOpen: Dispatch<SetStateAction<boolean>>;

  // ========== 輔助操作 ==========
  toggleMute: () => void;
  resetPlaybackState: () => void;
}

const DEFAULT_VOLUME = 0.7;
const DEFAULT_PLAYBACK_RATE = 1;

/**
 * 管理影片播放核心狀態的 Hook。
 */
export const useVideoPlayer = (
  config: UseVideoPlayerConfig = {}
): UseVideoPlayerReturn => {
  const {
    defaultVolume = DEFAULT_VOLUME,
    defaultPlaybackRate = DEFAULT_PLAYBACK_RATE,
  } = config;

  // ========== 播放狀態 ==========
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const isPlayingRef = useRef<boolean>(isPlaying);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const resetPlaybackState = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setPlayedSeconds(0);
  }, []);

  // ========== 進度與時間 ==========
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [playedSeconds, setPlayedSeconds] = useState<number>(0);
  const [currentFrame, setCurrentFrame] = useState<number>(0);

  // ========== 音量與速度 ==========
  const [volume, setVolumeState] = useState<number>(defaultVolume);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [playbackRate, setPlaybackRateState] = useState<number>(defaultPlaybackRate);

  const setVolume: Dispatch<SetStateAction<number>> = useCallback((value) => {
    setVolumeState((prevVolume) => {
      const nextVolume = typeof value === 'function' ? (value as (prev: number) => number)(prevVolume) : value;
      const clampedVolume = Math.min(1, Math.max(0, Number.isFinite(nextVolume) ? nextVolume : prevVolume));

      if (clampedVolume === 0) {
        setIsMuted(true);
      } else if (isMuted && clampedVolume > 0) {
        setIsMuted(false);
      }

      return clampedVolume;
    });
  }, [isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const setPlaybackRate: Dispatch<SetStateAction<number>> = useCallback((value) => {
    setPlaybackRateState((prevRate) => {
      const nextRate = typeof value === 'function' ? (value as (prev: number) => number)(prevRate) : value;
      const clampedRate = Math.min(4, Math.max(0.25, Number.isFinite(nextRate) ? nextRate : prevRate));
      return clampedRate;
    });
  }, []);

  // ========== 其他狀態 ==========
  const [savedTimecodes, setSavedTimecodes] = useState<Record<number, string>>({});
  const [bookmarkEnabled, setBookmarkEnabled] = useState<boolean>(false);
  const [embedUrl, setEmbedUrl] = useState<string>('');
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

  return {
    isPlaying,
    setIsPlaying,
    isPlayingRef,
    progress,
    setProgress,
    duration,
    setDuration,
    playedSeconds,
    setPlayedSeconds,
    currentFrame,
    setCurrentFrame,
    volume,
    setVolume,
    isMuted,
    setIsMuted,
    playbackRate,
    setPlaybackRate,
    savedTimecodes,
    setSavedTimecodes,
    bookmarkEnabled,
    setBookmarkEnabled,
    embedUrl,
    setEmbedUrl,
    settingsOpen,
    setSettingsOpen,
    toggleMute,
    resetPlaybackState,
  };
};

export default useVideoPlayer;


